import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { messageTemplateSendable, messageTemplateStatusStyle } from '../utils/metaTemplateStatus';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CloseIcon from '@mui/icons-material/Close';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { createClient, updateClient, sendWhatsAppMessage, fetchTemplates, setClientEngagementActive, fetchHypingProjects, parseHypingProjects, fetchTags, fetchWhatsAppCloudStatus, fetchAgentSettings, fetchCampaigns, MAX_HYPING_PROJECTS, MessageTemplate, Client, ClientTag, HypingProjectOption, STAGES, STAGE_LABELS, Stage } from '../services/api';
import {
  evaluateSevenDayReadiness,
  engagementDaysFromCampaignRows,
  readinessWarningText,
  CampaignReadiness,
} from '../utils/campaignReadiness';
import Alert from '@mui/material/Alert';

// The Hyping Projects the agent last selected are remembered so subsequent
// client entries default to them — an agent hyping the same projects at a launch
// shouldn't have to re-pick them for every walk-in.
const LAST_HYPING_PROJECT_KEY = 'superConnector.lastHypingProject';

function loadLastHypingProjects(): { id: string; name: string }[] {
  try {
    const raw = localStorage.getItem(LAST_HYPING_PROJECT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Tolerate the legacy single-object shape ({ id, name }) as well as the array.
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    return arr
      .filter((p) => p && typeof p.id === 'string' && typeof p.name === 'string')
      .slice(0, MAX_HYPING_PROJECTS);
  } catch { /* ignore */ }
  return [];
}

function saveLastHypingProjects(ps: { id: string; name: string }[]): void {
  try {
    if (ps.length > 0) localStorage.setItem(LAST_HYPING_PROJECT_KEY, JSON.stringify(ps));
    else localStorage.removeItem(LAST_HYPING_PROJECT_KEY);
  } catch { /* ignore */ }
}

const SAVE_TO_CONTACTS_KEY = 'superConnector.saveToContacts';

function loadSaveToContacts(): boolean {
  try {
    const raw = localStorage.getItem(SAVE_TO_CONTACTS_KEY);
    if (raw === null) return true; // default ON
    return raw === 'true';
  } catch { return true; }
}

function persistSaveToContacts(val: boolean): void {
  try { localStorage.setItem(SAVE_TO_CONTACTS_KEY, String(val)); } catch { /* ignore */ }
}

function saveToDeviceContacts(name: string, mobile: string, email: string): void {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${name}`,
    `N:${name};;;;`,
    mobile ? `TEL;TYPE=CELL:${mobile}` : '',
    email ? `EMAIL:${email}` : '',
    'END:VCARD',
  ].filter(Boolean).join('\r\n');

  // On iOS Safari and Android Chrome, opening a data:text/vcard URI directly
  // launches the native "Add to Contacts" sheet instead of downloading a file.
  const dataUri = `data:text/vcard;charset=utf-8,${encodeURIComponent(lines)}`;
  const a = document.createElement('a');
  a.href = dataUri;
  // No `download` attribute — lets the browser handle it as a content-type,
  // which triggers the native contacts UI on mobile.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

const DISTRICTS = Array.from({ length: 28 }, (_, i) => {
  const d = i + 1;
  const labels: Record<number, string> = {
    1: 'Raffles Place, Marina', 2: 'Chinatown, Tanjong Pagar', 3: 'Alexandra, Queenstown',
    4: 'Harbourfront, Telok Blangah', 5: 'Buona Vista, Clementi', 6: 'City Hall, Clarke Quay',
    7: 'Beach Road, Bugis', 8: 'Farrer Park, Little India', 9: 'Orchard, River Valley',
    10: 'Tanglin, Holland', 11: 'Newton, Novena', 12: 'Balestier, Toa Payoh',
    13: 'Macpherson, Potong Pasir', 14: 'Eunos, Geylang', 15: 'East Coast, Marine Parade',
    16: 'Bedok, Upper East Coast', 17: 'Changi, Loyang', 18: 'Pasir Ris, Tampines',
    19: 'Hougang, Punggol, Sengkang', 20: 'Ang Mo Kio, Bishan', 21: 'Clementi, Upper Bukit Timah',
    22: 'Boon Lay, Jurong', 23: 'Bukit Batok, Hillview', 24: 'Lim Chu Kang, Tengah',
    25: 'Admiralty, Woodlands', 26: 'Mandai, Upper Thomson', 27: 'Sembawang, Yishun',
    28: 'Seletar, Yio Chu Kang',
  };
  return { value: `D${d}`, label: `D${d} - ${labels[d] || ''}` };
});

const DISTANCE_OPTIONS = [1, 3, 5, 10] as const;

function formatDistanceLabel(distanceKm: number): string {
  return `Within ${distanceKm} km`;
}

function parseDistanceKm(value: unknown): number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) && value > 0 ? value : null;
  if (typeof value === 'string') {
    const direct = Number(value);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const m = /(\d+(?:\.\d+)?)\s*km/i.exec(value);
    if (!m) return null;
    const parsed = Number(m[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** When provided, the form runs in Edit mode and updates this client. */
  client?: Client | null;
  /** Blinks an orange glow around the drawer — used by the Why Super Connector tour. */
  highlight?: boolean;
}

const COUNTRY_CODES = [
  { code: '65', label: '+65 SG' },
  { code: '60', label: '+60 MY' },
  { code: '62', label: '+62 ID' },
  { code: '86', label: '+86 CN' },
  { code: '852', label: '+852 HK' },
  { code: '91', label: '+91 IN' },
  { code: '63', label: '+63 PH' },
  { code: '66', label: '+66 TH' },
  { code: '84', label: '+84 VN' },
  { code: '1', label: '+1 US/CA' },
  { code: '44', label: '+44 UK' },
  { code: '61', label: '+61 AU' },
];

// Parse a stored mobile (e.g. "+6598290620" or "98290620") into countryCode + digits.
function parseStoredMobile(full?: string | null): { code: string; digits: string } {
  if (!full) return { code: '65', digits: '' };
  const trimmed = full.trim();
  const cleaned = trimmed.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) {
    const all = cleaned.slice(1);
    const codes = COUNTRY_CODES.map((c) => c.code).sort((a, b) => b.length - a.length);
    for (const c of codes) {
      if (all.startsWith(c)) return { code: c, digits: all.slice(c.length) };
    }
    return { code: '65', digits: all };
  }
  return { code: '65', digits: cleaned };
}

const PROPERTY_TYPES = ['HDB', 'Condo', 'Landed', 'Commercial'];

interface OneMapResult {
  SEARCHVAL: string;
  BLK_NO?: string;
  ROAD_NAME?: string;
  BUILDING?: string;
  ADDRESS?: string;
  POSTAL?: string;
  X?: string;
  Y?: string;
  LATITUDE?: string;
  LONGITUDE?: string;
}

export default function QuickCreateClient({ open, onClose, onCreated, client, highlight = false }: Props) {
  const isEdit = !!client;
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('65');
  const [mobile, setMobile] = useState('');
  const [type, setType] = useState<string>('buyer');
  const [budget, setBudget] = useState('');
  const [propertyType, setPropertyType] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<string>('new');
  // 7-Day Engagement toggle. Defaults OFF on create; flips ON only when the
  // campaign is Ready / Meta-approved (same gate as Settings / ClientDetail).
  const [engagementActive, setEngagementActive] = useState(false);
  const [sevenDayReadiness, setSevenDayReadiness] = useState<CampaignReadiness>({ canActivate: false, reasons: [] });
  const [engagementConfirm, setEngagementConfirm] = useState<{ nextActive: boolean } | null>(null);
  const [engagementBusy, setEngagementBusy] = useState(false);
  const [saveToContacts, setSaveToContacts] = useState<boolean>(() => loadSaveToContacts());
  const [saving, setSaving] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);
  const [waMessage, setWaMessage] = useState('');
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  /** Official API: the intro must be an approved template, sent unchanged. */
  const [waUsingCloud, setWaUsingCloud] = useState(false);
  const [waTemplateId, setWaTemplateId] = useState<string | null>(null);

  // Hyping Projects — up to 3 projects the agent was promoting when capturing the client.
  const [hypingProjects, setHypingProjects] = useState<HypingProjectOption[]>([]);
  const [hypingProjectsLoading, setHypingProjectsLoading] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<HypingProjectOption[]>([]);
  // Client tags (any number). Options are all the agent's tags; value is the applied set.
  const [availableTags, setAvailableTags] = useState<ClientTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<ClientTag[]>([]);

  // Location fields
  const [currentLocation, setCurrentLocation] = useState('');
  const [currentLocationLat, setCurrentLocationLat] = useState<number | null>(null);
  const [currentLocationLng, setCurrentLocationLng] = useState<number | null>(null);
  const [preferredLocation, setPreferredLocation] = useState('');
  const [preferredLocationLat, setPreferredLocationLat] = useState<number | null>(null);
  const [preferredLocationLng, setPreferredLocationLng] = useState<number | null>(null);
  const [preferredLocationEditable, setPreferredLocationEditable] = useState(false);
  const [loadingLocation, setLoadingLocation] = useState(false);
  // Default to a 3 km radius for new clients so the agent only has to opt out of
  // the default rather than always opting in. Edit mode prefills from the client.
  const [showInterestedLocation, setShowInterestedLocation] = useState(false);
  const [distanceFromPreferredLocation, setDistanceFromPreferredLocation] = useState<number | null>(3);
  const [selectedDistricts, setSelectedDistricts] = useState<typeof DISTRICTS[number][]>([]);

  // OneMap location autocomplete state
  const [locationOptions, setLocationOptions] = useState<OneMapResult[]>([]);
  const [locationSearchInput, setLocationSearchInput] = useState('');
  const [locationSearching, setLocationSearching] = useState(false);

  const fetchGpsLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setCurrentLocation('GPS not supported');
      setCurrentLocationLat(null);
      setCurrentLocationLng(null);
      return;
    }
    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`
          );
          const data = await res.json();
          const addr = data.address;
          const locationName = [
            addr?.building || addr?.amenity || addr?.shop || '',
            addr?.road || '',
            addr?.suburb || addr?.neighbourhood || '',
          ].filter(Boolean).join(', ') || data.display_name || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setCurrentLocation(locationName);
          setCurrentLocationLat(latitude);
          setCurrentLocationLng(longitude);
          setPreferredLocation((prev) => prev.trim() || locationName);
          setPreferredLocationLat((prev) => prev ?? latitude);
          setPreferredLocationLng((prev) => prev ?? longitude);
        } catch {
          const fallback = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setCurrentLocation(fallback);
          setCurrentLocationLat(latitude);
          setCurrentLocationLng(longitude);
          setPreferredLocation((prev) => prev.trim() || fallback);
          setPreferredLocationLat((prev) => prev ?? latitude);
          setPreferredLocationLng((prev) => prev ?? longitude);
        }
        setLoadingLocation(false);
        setPreferredLocationEditable(false);
      },
      () => {
        setCurrentLocation('Location unavailable');
        setCurrentLocationLat(null);
        setCurrentLocationLng(null);
        setLoadingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  // Auto-fetch GPS when drawer opens for CREATE mode only
  useEffect(() => {
    if (open && !isEdit && !currentLocation && !loadingLocation) {
      fetchGpsLocation();
    }
  }, [open, isEdit, currentLocation, loadingLocation, fetchGpsLocation]);

  // Load the Sitemaps project list (same as Super Picker) whenever the drawer opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setHypingProjectsLoading(true);
    fetchHypingProjects()
      .then((list) => {
        if (cancelled) return;
        const sorted = [...list].sort((a, b) =>
          (a.projectName || '').localeCompare(b.projectName || '', undefined, { sensitivity: 'base' })
        );
        setHypingProjects(sorted);
      })
      .catch(() => { if (!cancelled) setHypingProjects([]); })
      .finally(() => { if (!cancelled) setHypingProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Default the Hyping Projects to the agent's last picks when creating a new client.
  useEffect(() => {
    if (!open || isEdit) return;
    const last = loadLastHypingProjects();
    if (last.length > 0) {
      setSelectedProjects(last.map((p) => ({ projectId: p.id, projectName: p.name })));
    }
  }, [open, isEdit]);

  // Load this agent's tags whenever the drawer opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchTags()
      .then((list) => { if (!cancelled) setAvailableTags(list); })
      .catch(() => { if (!cancelled) setAvailableTags([]); });
    return () => { cancelled = true; };
  }, [open]);

  // 7-Day readiness — drives create default + whether ON is allowed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [cloudStatus, settings, camps] = await Promise.all([
          fetchWhatsAppCloudStatus().catch(() => null),
          fetchAgentSettings().catch(() => null),
          fetchCampaigns().catch(() => null),
        ]);
        if (cancelled) return;
        const usingCloud = cloudStatus?.provider === 'cloud';
        setWaUsingCloud(usingCloud);
        const engagementRows = camps?.grouped?.['7-Day Engagement'] ?? [];
        const readiness = evaluateSevenDayReadiness({
          days: engagementDaysFromCampaignRows(engagementRows),
          agentSettings: settings,
          usingCloud: !!usingCloud,
          templates: cloudStatus?.templates ?? [],
        });
        setSevenDayReadiness(readiness);
        // Create mode follows the current sending method. Bridge only needs
        // each day configured; Official API also needs every day Meta-approved.
        if (!isEdit) {
          setEngagementActive(readiness.canActivate);
        }
      } catch {
        if (!cancelled) {
          setSevenDayReadiness({ canActivate: false, reasons: ['A day is not ready in Connector'] });
          if (!isEdit) setEngagementActive(false);
        }
      }
    };
    void load();
    const onProviderChanged = () => { void load(); };
    window.addEventListener('sc-setup-status-changed', onProviderChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('sc-setup-status-changed', onProviderChanged);
    };
  }, [open, isEdit]);

  // Prefill form when entering edit mode
  useEffect(() => {
    if (!open) return;
    if (client) {
      const m = parseStoredMobile(client.Mobile);
      setName(client.Name || '');
      setCountryCode(m.code);
      setMobile(m.digits);
      setType(client.Type || 'buyer');
      setBudget(client.Budget != null ? String(client.Budget) : '');
      setPropertyType(
        client.PropertyType ? client.PropertyType.split(',').map((s) => s.trim()).filter(Boolean) : []
      );
      setNote(client.Note || '');
      setEmail(client.Email || '');
      setStage(client.Stage || 'new');
      setEngagementActive(client.EngagementActive ?? false);
      setSelectedProjects(
        parseHypingProjects(client).map((r) => ({ projectId: r.id, projectName: r.name }))
      );
      setSelectedTags(
        Array.isArray(client.Tags)
          ? client.Tags.map((t) => ({ Id: t.Id, Name: t.Name, Color: t.Color ?? null }))
          : []
      );
      setCurrentLocation(client.CurrentLocation || '');
      setCurrentLocationLat(client.CurrentLocationLat ?? null);
      setCurrentLocationLng(client.CurrentLocationLng ?? null);
      setPreferredLocation(client.PreferredLocation || client.CurrentLocation || '');
      setPreferredLocationLat(client.PreferredLocationLat ?? client.CurrentLocationLat ?? null);
      setPreferredLocationLng(client.PreferredLocationLng ?? client.CurrentLocationLng ?? null);
      setPreferredLocationEditable(false);
      setLoadingLocation(false);
      setDistanceFromPreferredLocation(
        parseDistanceKm(client.DistanceFromPreferredLocation ?? (client as Client & { DistanceFromCurrent?: unknown }).DistanceFromCurrent)
      );
      // InterestedDistricts is stored as JSON string on the wire. Parse and map back to option objects.
      try {
        const codes: string[] = client.InterestedDistricts
          ? JSON.parse(client.InterestedDistricts)
          : [];
        const matched = DISTRICTS.filter((d) => codes.includes(d.value));
        setSelectedDistricts(matched);
        if (matched.length > 0 || client.DistanceFromPreferredLocation != null) {
          setShowInterestedLocation(true);
        }
      } catch {
        setSelectedDistricts([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client?.Id]);

  // Search OneMap when in editable mode
  useEffect(() => {
    if (!preferredLocationEditable) return;
    const q = locationSearchInput.trim();
    if (!q) {
      setLocationOptions([]);
      return;
    }
    let cancelled = false;
    setLocationSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://www.onemap.gov.sg/api/common/elastic/search?searchVal=${encodeURIComponent(q)}&returnGeom=Y&getAddrDetails=Y&pageNum=1`
        );
        const data = await res.json();
        if (!cancelled) {
          setLocationOptions(Array.isArray(data?.results) ? data.results : []);
        }
      } catch {
        if (!cancelled) setLocationOptions([]);
      } finally {
        if (!cancelled) setLocationSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [locationSearchInput, preferredLocationEditable]);

  const formatOneMapLabel = (r: OneMapResult): string => {
    const parts = [r.BUILDING && r.BUILDING !== 'NIL' ? r.BUILDING : '', r.BLK_NO, r.ROAD_NAME].filter(Boolean);
    const head = parts.join(' ').trim();
    const tail = r.POSTAL && r.POSTAL !== 'NIL' ? ` (${r.POSTAL})` : '';
    return (head || r.SEARCHVAL || r.ADDRESS || '') + tail;
  };

  const reset = () => {
    setName('');
    setCountryCode('65');
    setMobile('');
    setType('buyer');
    setBudget('');
    setPropertyType([]);
    setNote('');
    setEmail('');
    setStage('new');
    setEngagementActive(sevenDayReadiness.canActivate);
    // Keep the last-hyped projects as the default for the next client entry.
    setSelectedProjects(
      loadLastHypingProjects().map((p) => ({ projectId: p.id, projectName: p.name }))
    );
    setSelectedTags([]);
    setCreatedClientId(null);
    setShowWhatsApp(false);
    setWaMessage('');
    setWaTemplateId(null);
    setCurrentLocation('');
    setCurrentLocationLat(null);
    setCurrentLocationLng(null);
    setPreferredLocation('');
    setPreferredLocationLat(null);
    setPreferredLocationLng(null);
    setPreferredLocationEditable(false);
    setLoadingLocation(false);
    setShowInterestedLocation(false);
    setDistanceFromPreferredLocation(3);
    setSelectedDistricts([]);
    setLocationOptions([]);
    setLocationSearchInput('');
  };

  // Build E.164 mobile (+<cc><digits>) for storage / WA sending
  const buildFullMobile = (): string => {
    const digits = mobile.replace(/\D/g, '');
    if (!digits) return '';
    return `+${countryCode}${digits}`;
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const fullMobile = buildFullMobile();
      const projectRefs = selectedProjects.map((p) => ({ id: p.projectId, name: p.projectName }));
      const primaryRef = projectRefs[0];
      const payload = {
        Name: name.trim(),
        Mobile: fullMobile || undefined,
        Type: type || undefined,
        Budget: budget ? parseFloat(budget) : undefined,
        PropertyType: propertyType.length > 0 ? propertyType.join(',') : undefined,
        Note: note || undefined,
        Email: email.trim() || undefined,
        PreferredLocation: preferredLocation.trim() || undefined,
        PreferredLocationLat: preferredLocationLat,
        PreferredLocationLng: preferredLocationLng,
        // Send the full list plus the primary mirrored onto the legacy fields.
        HypingProjects: projectRefs,
        HypingProjectId: primaryRef?.id || undefined,
        HypingProject: primaryRef?.name || undefined,
        // Tags (any number). Always sent so clearing all tags persists on edit.
        TagIds: selectedTags.map((t) => t.Id),
      } as any;

      const locationPayload = {
        CurrentLocation: currentLocation.trim() || null,
        CurrentLocationLat: currentLocationLat,
        CurrentLocationLng: currentLocationLng,
        InterestedDistricts: selectedDistricts.length > 0 ? selectedDistricts.map((d) => d.value) : null,
        DistanceFromPreferredLocation: distanceFromPreferredLocation,
      };

      let saved: Client;
      if (isEdit && client) {
        saved = await updateClient(client.Id, {
          ...payload,
          ...locationPayload,
          Stage: stage,
          EngagementActive: engagementActive,
        } as any);
      } else {
        saved = await createClient({
          ...payload,
          CurrentLocation: locationPayload.CurrentLocation || undefined,
          CurrentLocationLat: locationPayload.CurrentLocationLat,
          CurrentLocationLng: locationPayload.CurrentLocationLng,
          InterestedDistricts: locationPayload.InterestedDistricts || undefined,
          DistanceFromPreferredLocation: locationPayload.DistanceFromPreferredLocation ?? undefined,
          EngagementActive: engagementActive,
        });
      }
      setCreatedClientId(saved.Id);

      if (!isEdit && saveToContacts && (fullMobile || email.trim())) {
        try { saveToDeviceContacts(name.trim(), fullMobile, email.trim()); } catch (e) { console.warn('save to contacts failed', e); }
      }

      if (!isEdit && fullMobile) {
        // Load templates for WhatsApp prompt
        try {
          const tpls = await fetchTemplates('whatsapp');
          setTemplates(tpls);
        } catch {}
        setShowWhatsApp(true);
      } else {
        reset();
        onCreated();
        onClose();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [whatsAppError, setWhatsAppError] = useState<string | null>(null);

  const handleSendWhatsApp = async () => {
    const fullMobile = buildFullMobile();
    if (!createdClientId || !fullMobile || !waMessage.trim()) return;
    if (waUsingCloud && !waTemplateId) return;
    setSendingWhatsApp(true);
    setWhatsAppError(null);
    try {
      // Sends through the agent's paired bridge or, on the Official API, the
      // approved template (server-side logs the activity), so the agent never
      // leaves the app.
      await sendWhatsAppMessage({
        clientId: createdClientId,
        message: waMessage,
        mobile: fullMobile,
        templateId: waUsingCloud ? waTemplateId ?? undefined : undefined,
      });
      reset();
      onCreated();
    } catch (e) {
      console.error(e);
      setWhatsAppError(e instanceof Error ? e.message : 'Failed to send WhatsApp');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSkipWhatsApp = () => {
    reset();
    onCreated();
  };

  // Ensure every current selection is a valid option even if it isn't in the
  // fetched list (e.g. a remembered default whose project dropped off the
  // top-viewing list, or a project a saved client was tagged with).
  const missingSelected = selectedProjects.filter(
    (s) => !hypingProjects.some((p) => p.projectId === s.projectId)
  );
  const hypingOptions: HypingProjectOption[] = missingSelected.length > 0
    ? [...missingSelected, ...hypingProjects]
    : hypingProjects;
  const projectLimitReached = selectedProjects.length >= MAX_HYPING_PROJECTS;

  return (
    <>
      <Drawer
        anchor="right"
        open={open && !showWhatsApp}
        onClose={() => { reset(); onClose(); }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
            zIndex: 1300,
            ...(highlight && {
              animation: 'qccHighlightBlink 1.3s ease-in-out infinite',
              '@keyframes qccHighlightBlink': {
                '0%, 100%': {
                  boxShadow: 'inset 0 0 0 2px rgba(237, 137, 54, 0.95), -10px 0 24px rgba(237, 137, 54, 0.1)',
                },
                '50%': {
                  boxShadow: 'inset 0 0 0 5px rgba(237, 137, 54, 0.35), -10px 0 42px rgba(237, 137, 54, 0.55)',
                },
              },
            }),
          },
        }}
        sx={{ zIndex: 1300 }}
      >
        {/* Drawer header with close button */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2,
          pb: 1,
        }}>
          <Typography variant="h6" fontWeight={700}>{isEdit ? 'Edit Client' : 'Quick Create Client'}</Typography>
          <IconButton onClick={() => { reset(); onClose(); }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pb: 3, overflowY: 'auto', flex: 1 }}>
          {/* Hyping Activity — what the agent is promoting right now. Kept at the
              top so the project + location are captured before client details. */}
          <Box sx={{
            mb: 3,
            p: 2,
            border: '1px solid var(--color-border)',
            borderRadius: 1,
            bgcolor: 'var(--color-bg)',
          }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
              Hyping Activity
            </Typography>

            {/* Current Location - GPS based */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 0.5 }}>
                Current Location
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.2,
                border: '1px solid var(--color-border)',
                borderRadius: 1,
                bgcolor: 'var(--color-surface)',
                minHeight: 40,
              }}>
                <MyLocationIcon sx={{ color: '#ed8936', fontSize: 20, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    color: currentLocation ? 'var(--color-text-strong)' : 'var(--color-text-faint)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loadingLocation ? 'Detecting location...' : currentLocation || 'Location unavailable'}
                </Typography>
                {loadingLocation ? (
                  <CircularProgress size={18} sx={{ color: '#ed8936', flexShrink: 0 }} />
                ) : null}
              </Box>
            </Box>

            {/* Hyping Projects — sourced from Super Picker's Sitemaps list. Up to 3. */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 0.5 }}>
                Hyping Projects (up to {MAX_HYPING_PROJECTS})
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={hypingOptions}
                loading={hypingProjectsLoading}
                value={selectedProjects}
                getOptionLabel={(opt) => opt.projectName || ''}
                isOptionEqualToValue={(opt, val) => opt.projectId === val.projectId}
                getOptionDisabled={(opt) =>
                  projectLimitReached && !selectedProjects.some((s) => s.projectId === opt.projectId)
                }
                renderOption={(props, option, { selected }) => {
                  const { key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
                  return (
                    <li {...liProps} key={option.projectId}>
                      <Checkbox
                        size="small"
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        checked={selected}
                        sx={{ mr: 1, p: 0.5, color: 'var(--color-text-faint)', '&.Mui-checked': { color: '#ed8936' } }}
                      />
                      {option.projectName}
                    </li>
                  );
                }}
                onChange={(_, val) => {
                  const capped = val.slice(0, MAX_HYPING_PROJECTS);
                  setSelectedProjects(capped);
                  saveLastHypingProjects(capped.map((p) => ({ id: p.projectId, name: p.projectName })));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.projectId}
                      label={option.projectName}
                      size="small"
                      sx={{ bgcolor: '#ed8936', color: '#fff' }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedProjects.length === 0
                        ? `Select up to ${MAX_HYPING_PROJECTS} projects`
                        : projectLimitReached
                          ? ''
                          : 'Add another'
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {hypingProjectsLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Tags — any number, from the agent's Manage Tags list */}
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 0.5 }}>
                Tags
              </Typography>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size="small"
                options={availableTags}
                value={selectedTags}
                getOptionLabel={(opt) => opt.Name || ''}
                isOptionEqualToValue={(opt, val) => opt.Id === val.Id}
                noOptionsText="No tags yet — create them in Settings → Manage Tags"
                renderOption={(props, option, { selected }) => {
                  const { key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
                  return (
                    <li {...liProps} key={option.Id}>
                      <Checkbox
                        size="small"
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        checked={selected}
                        sx={{ mr: 1, p: 0.5, color: 'var(--color-text-faint)', '&.Mui-checked': { color: option.Color || '#ed8936' } }}
                      />
                      <Chip
                        label={option.Name}
                        size="small"
                        sx={{ bgcolor: `${option.Color || '#ed8936'}22`, color: option.Color || '#ed8936', fontWeight: 700 }}
                      />
                    </li>
                  );
                }}
                onChange={(_, val) => setSelectedTags(val)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.Id}
                      label={option.Name}
                      size="small"
                      sx={{ bgcolor: `${option.Color || '#ed8936'}22`, color: option.Color || '#ed8936', fontWeight: 700 }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selectedTags.length === 0 ? 'Add tags' : ''}
                  />
                )}
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Client Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            sx={{ mb: 2 }}
            placeholder="e.g. John Doe"
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl sx={{ minWidth: 110 }}>
              <InputLabel>Code</InputLabel>
              <Select
                value={countryCode}
                label="Code"
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {COUNTRY_CODES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Mobile Number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="81234567"
              inputMode="numeric"
            />
          </Box>

          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
            type="email"
          />

          <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 1 }}>Type</Typography>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, v) => v && setType(v)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value="buyer" sx={{ textTransform: 'none', flex: 1 }}>Buyer</ToggleButton>
            <ToggleButton value="seller" sx={{ textTransform: 'none', flex: 1 }}>Seller</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            fullWidth
            label="Budget ($)"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            type="number"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Property Type</InputLabel>
            <Select
              multiple
              value={propertyType}
              onChange={(e) => {
                const v = e.target.value;
                setPropertyType(typeof v === 'string' ? v.split(',') : (v as string[]));
              }}
              label="Property Type"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" sx={{ bgcolor: '#ed8936', color: '#fff' }} />
                  ))}
                </Box>
              )}
            >
              {PROPERTY_TYPES.map((pt) => (
                <MenuItem key={pt} value={pt}>{pt}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Preferred Location - editable, defaults to Current Location */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--color-text-subtle)', mb: 0.5 }}>
              Preferred Location
            </Typography>
            {preferredLocationEditable ? (
              <Autocomplete
                fullWidth
                size="small"
                freeSolo
                options={locationOptions}
                filterOptions={(x) => x}
                loading={locationSearching}
                inputValue={locationSearchInput}
                onInputChange={(_, v) => setLocationSearchInput(v)}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : formatOneMapLabel(opt)}
                isOptionEqualToValue={(opt, val) =>
                  typeof opt !== 'string' && typeof val !== 'string' && opt.SEARCHVAL === val.SEARCHVAL && opt.POSTAL === val.POSTAL
                }
                onChange={(_, val) => {
                  if (!val) return;
                  const text = typeof val === 'string' ? val : formatOneMapLabel(val);
                  setPreferredLocation(text);
                  const lat = typeof val === 'string' ? null : Number(val.LATITUDE);
                  const lng = typeof val === 'string' ? null : Number(val.LONGITUDE);
                  setPreferredLocationLat(lat != null && Number.isFinite(lat) ? lat : null);
                  setPreferredLocationLng(lng != null && Number.isFinite(lng) ? lng : null);
                  setPreferredLocationEditable(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder="Search address (OneMap)"
                    onChange={(e) => {
                      setLocationSearchInput(e.target.value);
                      setPreferredLocation(e.target.value);
                      setPreferredLocationLat(null);
                      setPreferredLocationLng(null);
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {locationSearching ? <CircularProgress size={16} /> : null}
                          <IconButton size="small" onClick={() => setPreferredLocationEditable(false)} title="Done">
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => { fetchGpsLocation(); setPreferredLocationEditable(false); }} title="Use Current">
                            <MyLocationIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.2,
                border: '1px solid var(--color-border)',
                borderRadius: 1,
                bgcolor: 'var(--color-bg)',
                minHeight: 40,
              }}>
                <MyLocationIcon sx={{ color: '#ed8936', fontSize: 20, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    color: preferredLocation ? 'var(--color-text-strong)' : 'var(--color-text-faint)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {preferredLocation || 'Location unavailable'}
                </Typography>
                <Button
                  size="small"
                  onClick={() => { setLocationSearchInput(preferredLocation || ''); setPreferredLocationEditable(true); }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    color: '#ed8936',
                    minWidth: 'auto',
                    px: 1,
                    flexShrink: 0,
                  }}
                >
                  Change
                </Button>
              </Box>
            )}
          </Box>

          {/* Interested Location */}
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              onClick={() => setShowInterestedLocation(!showInterestedLocation)}
              endIcon={showInterestedLocation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: 'none',
                justifyContent: 'space-between',
                color: 'var(--color-text-strong)',
                bgcolor: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                borderRadius: 1,
                px: 2,
                py: 1.2,
                '&:hover': { bgcolor: 'var(--color-surface-hover, rgba(255,255,255,0.05))' },
              }}
            >
              Interested Location
              {(selectedDistricts.length > 0 || distanceFromPreferredLocation != null) && (
                <Chip
                  label={
                    [
                      selectedDistricts.length > 0 ? `${selectedDistricts.length} district${selectedDistricts.length > 1 ? 's' : ''}` : '',
                      distanceFromPreferredLocation != null ? formatDistanceLabel(distanceFromPreferredLocation) : '',
                    ].filter(Boolean).join(', ')
                  }
                  size="small"
                  sx={{ ml: 1, bgcolor: '#ed8936', color: '#fff', fontSize: '0.7rem' }}
                />
              )}
            </Button>

            <Collapse in={showInterestedLocation}>
              <Box sx={{
                border: '1px solid var(--color-border)',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}>
                {/* Distance from preferred location (km) — Autocomplete gives a built-in clear button. */}
                <Autocomplete
                  size="small"
                  options={DISTANCE_OPTIONS}
                  value={distanceFromPreferredLocation}
                  onChange={(_, newVal) => setDistanceFromPreferredLocation(newVal ?? null)}
                  getOptionLabel={(opt) => formatDistanceLabel(opt)}
                  renderInput={(params) => (
                    <TextField {...params} label="Distance from preferred location (km)" placeholder="Select a distance" />
                  )}
                />

                {/* District picker */}
                <Autocomplete
                  multiple
                  size="small"
                  options={DISTRICTS}
                  value={selectedDistricts}
                  onChange={(_, newVal) => setSelectedDistricts(newVal)}
                  getOptionLabel={(opt) => opt.label}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.value}
                        size="small"
                        sx={{ bgcolor: '#ed8936', color: '#fff' }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="District (D1–D28)" placeholder="Select districts" />
                  )}
                />

              </Box>
            </Collapse>
          </Box>

          <TextField
            fullWidth
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          {isEdit && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Stage</InputLabel>
              <Select value={stage} label="Stage" onChange={(e) => setStage(e.target.value)}>
                {STAGES.map((s) => (
                  <MenuItem key={s} value={s}>{STAGE_LABELS[s as Stage]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Save to Contacts — only shown in create mode; preference is remembered. */}
          {!isEdit && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.2,
              mb: 2,
              border: '1px solid var(--color-border)',
              borderRadius: 1,
              bgcolor: 'var(--color-bg)',
            }}>
              <ContactPhoneIcon sx={{ color: saveToContacts ? '#ed8936' : 'var(--color-text-faint)' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>Save to Contacts</Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                  {saveToContacts
                    ? 'Will download a vCard to add to your contact book'
                    : 'Contact will not be saved to your phone'}
                </Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={saveToContacts}
                    onChange={(_, checked) => {
                      setSaveToContacts(checked);
                      persistSaveToContacts(checked);
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                    }}
                  />
                }
                label=""
              />
            </Box>
          )}

          {/* 7-Day Engagement quick toggle. Always confirms; in edit mode the
              choice is persisted immediately so it stays in sync with the list. */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.2,
            mb: !engagementActive && !sevenDayReadiness.canActivate ? 1 : 3,
            border: '1px solid var(--color-border)',
            borderRadius: 1,
            bgcolor: 'var(--color-bg)',
          }}>
            <AutorenewIcon sx={{ color: engagementActive ? '#ed8936' : 'var(--color-text-faint)' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>7-Day Engagement</Typography>
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                {engagementActive
                  // On create, Day 1 is queued by the 08:00 planner, so the first
                  // message goes out the next morning — say so, or the agent waits
                  // for a message today that was never going to arrive.
                  ? (isEdit
                    ? 'Auto WhatsApp sequence is active for this client'
                    : 'Auto WhatsApp sequence starts tomorrow morning')
                  : 'Auto WhatsApp sequence is paused for this client'}
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={engagementActive}
                  disabled={!engagementActive && !sevenDayReadiness.canActivate}
                  onChange={(_, checked) => {
                    if (checked && !sevenDayReadiness.canActivate) return;
                    setEngagementConfirm({ nextActive: checked });
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#ed8936' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ed8936' },
                  }}
                />
              }
              label=""
            />
          </Box>
          {!engagementActive && !sevenDayReadiness.canActivate && (
            <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mb: 3 }}>
              {readinessWarningText(sevenDayReadiness.reasons) || '7-Day Engagement is not ready yet'}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={!name.trim() || saving}
              sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
            >
              {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save' : 'Create Client')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { reset(); onClose(); }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* WhatsApp Prompt Dialog */}
      <Dialog
        open={showWhatsApp}
        onClose={handleSkipWhatsApp}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Send personalized WhatsApp intro?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
            Send a personalized message to {name} via WhatsApp
          </Typography>

          {templates.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', mb: 0.5, display: 'block' }}>
                {waUsingCloud ? 'Approved templates:' : 'Quick templates:'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {templates.map((t) => {
                  const sendable = !waUsingCloud || messageTemplateSendable(t);
                  const chip = (
                    <Chip
                      key={t.Id}
                      label={t.Title}
                      size="small"
                      disabled={!sendable}
                      color={waUsingCloud && waTemplateId === t.Id ? 'success' : 'default'}
                      onClick={() => {
                        if (!sendable) return;
                        setWaTemplateId(t.Id);
                        setWaMessage(waUsingCloud ? t.Body : t.Body.replace(/\{\{client_name\}\}/gi, name));
                      }}
                      sx={{ cursor: sendable ? 'pointer' : 'not-allowed' }}
                    />
                  );
                  return sendable ? chip : (
                    <Tooltip key={t.Id} title={messageTemplateStatusStyle(t).hint}>
                      <span>{chip}</span>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}

          {waUsingCloud && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--color-text-faint)' }}>
              The Official API only sends Meta-approved templates, unchanged. Each message is estimated at S$0.10
              and counts toward your daily budget.
            </Typography>
          )}

          {waUsingCloud ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: '1px solid var(--color-border)',
                bgcolor: 'var(--color-bg)',
                whiteSpace: 'pre-wrap',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: waMessage ? 'var(--color-text-strong)' : 'var(--color-text-faint)',
                minHeight: 96,
              }}
            >
              {waMessage || 'Pick an approved template above'}
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              placeholder={`Hi ${name}! Thank you for your interest. I'm your dedicated property agent and I'd love to help you find your dream home...`}
            />
          )}
          {whatsAppError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#ef4444' }}>
              {whatsAppError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleSkipWhatsApp}
            disabled={sendingWhatsApp}
            sx={{ textTransform: 'none', color: 'var(--color-text-subtle)' }}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            startIcon={sendingWhatsApp ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <WhatsAppIcon />}
            onClick={handleSendWhatsApp}
            disabled={sendingWhatsApp || !waMessage.trim() || (waUsingCloud && !waTemplateId)}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, textTransform: 'none' }}
          >
            {sendingWhatsApp ? 'Sending...' : 'Send via WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Engagement confirmation — required by spec for both create & edit */}
      <Dialog
        open={!!engagementConfirm}
        onClose={() => !engagementBusy && setEngagementConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle fontWeight={700}>
          {engagementConfirm?.nextActive ? 'Turn ON 7-Day Engagement?' : 'Turn OFF 7-Day Engagement?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
            {engagementConfirm?.nextActive
              ? 'The system will start sending the 7-day WhatsApp sequence to this client (one message per day during your configured window).'
              : 'The system will stop sending automated 7-day messages to this client. You can resume any time.'}
          </Typography>
          {engagementConfirm?.nextActive && !sevenDayReadiness.canActivate && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {readinessWarningText(sevenDayReadiness.reasons)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEngagementConfirm(null)}
            disabled={engagementBusy}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={engagementBusy || (!!engagementConfirm?.nextActive && !sevenDayReadiness.canActivate)}
            onClick={async () => {
              if (!engagementConfirm) return;
              const next = engagementConfirm.nextActive;
              if (next && !sevenDayReadiness.canActivate) return;
              setEngagementBusy(true);
              try {
                // Persist immediately in edit mode so the list stays in sync.
                if (isEdit && client) {
                  await setClientEngagementActive(client.Id, next);
                }
                setEngagementActive(next);
                setEngagementConfirm(null);
              } catch (e) {
                console.error(e);
              } finally {
                setEngagementBusy(false);
              }
            }}
            sx={{ bgcolor: '#ed8936', '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none' }}
          >
            {engagementBusy ? 'Saving...' : engagementConfirm?.nextActive ? 'Turn ON' : 'Turn OFF'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
